import { type SchedulerEventModel, type SchedulerProConfig } from '@bryntum/schedulerpro'
import { addSeconds, differenceInSeconds, parse } from 'date-fns/esm'

import { getMainSelection } from '@/atoms'
import { type MoveOrdersPayload } from '@/components/modals/MoveOrdersModal'
import { moveOrderStep } from '@/features/domain/order'
import { selectDayRangeInfo } from '@/features/domain/scheduler'
import { store } from '@/store'
import { toast } from 'react-toastify'

type MoveOrderStepPayload = Parameters<typeof moveOrderStep>[0] & {
  paired?: uui.domain.client.rm.SchedulerEventPairedData
  edgeSeconds?: number
  status: uui.domain.client.rm.OrderStepTrackingData['status']
  executed: boolean
}

function sortEventsByStartDate(a: SchedulerEventModel, b: SchedulerEventModel) {
  // @ts-expect-error
  return a.startDate - b.startDate
}

function isCompatibleStep(event: SchedulerEventModel) {
  const type = event.getData('type') as uui.domain.client.rm.SchedulerEvent['type']
  const isUnApproved = event.getData('isApproved') === false
  const isCompatible = ['pickup', 'delivery', 'brk'].includes(type)
  return isUnApproved && isCompatible
}

function computeMoveOrdersPayload(payload: MoveOrderStepPayload): MoveOrdersPayload | undefined {
  const { edgeSeconds, status, executed, seconds, sourceRouteId, orderStepId, routeId, paired } =
    payload

  const isExecutedOrderStep = executed === true || status === 'reschedule'
  const isPairedOnTheSameRoute = paired && routeId === paired.pairedRouteId
  const gotEdge = edgeSeconds !== undefined
  const movingExecutedOrderAfterEdge = isExecutedOrderStep && gotEdge && seconds >= edgeSeconds

  // If the order step is not executed and not paired, we don't need to show the modal
  const isSingleNotExecutedOrder = !isExecutedOrderStep && !paired
  if (isSingleNotExecutedOrder) return

  // If the order step is paired, executed, not moving after the edge,
  // and the destination route is the same as the paired route, we don't need to show the modal
  if (paired && !movingExecutedOrderAfterEdge && isPairedOnTheSameRoute) {
    return
  }

  // Create base payload that's common for all cases
  const basePayload = {
    orderStepId,
    sourceRouteId,
    targetRouteId: routeId,
    seconds,
  }

  // If the order step is not paired,
  // or it's executed and moving after the edge, and the route is the same as the paired route,
  // we need to compute the payload without pairing information, since the paired won't be moved
  // but the execution events will be deleted
  if (
    !paired ||
    (paired && isExecutedOrderStep && movingExecutedOrderAfterEdge && isPairedOnTheSameRoute)
  ) {
    return {
      ...basePayload,
      paired: false,
    }
  }

  // For valid paired moves
  return {
    ...basePayload,
    paired: true,
    pairedExecuted: paired.pairedExecuted,
    pairedOrderStepId: paired.pairedOrderStepId,
    pairedOrderRouteId: paired.pairedRouteId,
  }
}

export function createOnBeforeEventDropFinalize(
  showMoveOrdersModal: (payload: MoveOrdersPayload) => void,
): SchedulerProConfig['onBeforeEventDropFinalize'] {
  return async event => {
    const {
      domEvent,
      // @ts-expect-error
      context: { origStart, newResource, eventRecords },
      source,
    } = event

    // eslint-disable-next-line no-param-reassign
    event.context.async = true

    const targetLocked = newResource.getData('routeLock')
    const targetAvailable = newResource.getData('available')
    const selectedOrderSteps = getMainSelection(true).orderSteps

    if (eventRecords.length !== 1 || targetLocked || !targetAvailable) {
      // eslint-disable-next-line no-param-reassign
      event.context.valid = false
      event.context.finalize(false)
      return
    }

    const { dayStartOffset } = selectDayRangeInfo(store.getState())

    const eventRecord = eventRecords[0]
    const resourceRecord = source.resourceStore.getById(eventRecord.resourceId)

    const day = parse(newResource.getData('dateAsString'), 'yyyyMMdd', new Date())
    day.setHours(0, 0, 0)

    const edgeStartDate = newResource.events.find(e => e.getData('type') === 'edge')?.startDate as
      | Date
      | undefined
    const edgeSeconds = edgeStartDate ? differenceInSeconds(edgeStartDate, day) : undefined

    let mouseDate = source.getDateFromDomEvent(domEvent)
    const eventDropSeconds = differenceInSeconds(mouseDate, source.startDate)

    // Since the mouse event is always set on the first day of calendar range, we need to set the date to the correct day.
    mouseDate.setFullYear(day.getFullYear(), day.getMonth(), day.getDate())
    // And the correct hours (seconds from midnight to drop point)
    mouseDate.setHours(0, 0, 0)
    mouseDate = addSeconds(mouseDate, dayStartOffset + eventDropSeconds)

    const seconds = differenceInSeconds(mouseDate, day)
    const routeId = newResource.id as string
    const orderStepId =
      selectedOrderSteps.length === 1 && eventRecord.getData('steps').length > 1
        ? selectedOrderSteps[0]
        : (eventRecord.id as string)
    const sourceRouteId = resourceRecord.id as string

    const paired = eventRecord.getData('pairedStepData') as
      | uui.domain.client.rm.SchedulerEventPairedData
      | undefined

    // If the poisition is the same as the original, we don't need to do anything.
    const sameRoute = routeId === sourceRouteId

    if (sameRoute) {
      const routeEvents = newResource.events
        .filter(e => e.id !== orderStepId && isCompatibleStep(e))
        .sort(sortEventsByStartDate) as SchedulerEventModel[]

      const newNextIndex = routeEvents.findIndex(e => {
        let eventStartDate = new Date(e.startDate)
        const eventSeconds = differenceInSeconds(eventStartDate, source.startDate)

        // Since the mouse event is always set on the first day of calendar range, we need to set the date to the correct day.
        eventStartDate.setFullYear(day.getFullYear(), day.getMonth(), day.getDate())

        // And the correct hours (seconds from midnight to the start of the event)
        eventStartDate.setHours(0, 0, 0)
        eventStartDate = addSeconds(eventStartDate, dayStartOffset + eventSeconds)
        return eventStartDate > mouseDate
      })
      const originalNextIndex = routeEvents.findIndex(e => e.startDate > origStart)

      if (originalNextIndex === newNextIndex) {
        event.context.finalize(false)
        return
      }
    }

    const executed = eventRecord.getData('executed') as boolean
    const status = eventRecord.getData(
      'status',
    ) as uui.domain.client.rm.OrderStepTrackingData['status']

    const moveOrdersPayload = computeMoveOrdersPayload({
      paired,
      status,
      seconds,
      routeId,
      executed,
      edgeSeconds,
      orderStepId,
      sourceRouteId,
    })

    if (moveOrdersPayload) {
      showMoveOrdersModal(moveOrdersPayload)
      event.context.finalize(false)

      return
    }

    try {
      const result = await store.dispatch(
        moveOrderStep({
          orderStepId,
          sourceRouteId,
          routeId,
          seconds,
        }),
      )
      if (moveOrderStep.rejected.match(result)) {
        throw new Error(result.payload?.message ?? 'Internal error')
      }
      event.context.finalize(false)
    } catch (error) {
      toast.error(error.message)
      event.context.finalize(false)
    }
  }
}
